/**
 * API Client
 * Handles authenticated API requests to the backend
 */

import { devLog } from '../utils/devLog';
import { resolvedAuthUser } from './resolvedAuthUser';
import { getApiUrl } from '../config/api';
import {
  applyDealerTenantToPayload,
  applyDealerTenantToUrl,
  dealerAdminHeaders,
} from '../pages/Admin/dealerAdminSession';
import {
  applySupportTenantToUrl,
  supportHeadersForEndpoint,
} from '../pages/Admin/supportedTenantsSession';


/**
 * Make an authenticated API request to the Flask backend
 * @param {string} endpoint - API endpoint (e.g., '/api/test/send-email')
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} - Fetch response
 */
/**
 * Make an authenticated API request using Firebase auth token
 * @param {string} endpoint - API endpoint (e.g., '/api/users/list') - Always use '/api/...' paths
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} - Fetch response
 */
export const authenticatedFetch = async (endpoint, options = {}) => {
  const { authUser, forceTokenRefresh, ...fetchOptions } = options;
  const user = authUser || await resolvedAuthUser();
  if (!user) {
    throw new Error('User not authenticated');
  }
  const token = await user.getIdToken(Boolean(forceTokenRefresh));

  // Check if this is a legacy endpoint (not using /api/ prefix)
  if (!endpoint.startsWith('/api/')) {
    endpoint = `/api${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`;
  }

  // Always use relative URLs with Firebase Hosting rewrites
  const url = getApiUrl(applySupportTenantToUrl(applyDealerTenantToUrl(endpoint)));

  // Determine if this is a JSON request
  const isJsonRequest =
    options.body && typeof options.body === 'string' && options.body.startsWith('{');

  // Set default headers for JSON content if needed
  const defaultHeaders = {
    // Only set Content-Type if it's a JSON request and not already set
    ...(isJsonRequest && !options.headers?.['Content-Type']
      ? { 'Content-Type': 'application/json' }
      : {}),
    // Add Accept header for JSON responses
    Accept: 'application/json',
  };

  let requestBody = options.body;
  if (isJsonRequest && typeof requestBody === 'string') {
    try {
      requestBody = JSON.stringify(applyDealerTenantToPayload(JSON.parse(requestBody)));
    } catch {
      requestBody = options.body;
    }
  }

  return fetch(url, {
    ...fetchOptions,
    body: requestBody,
    // Include credentials if backend uses cookies for any session features
    credentials: options.credentials || 'include',
    headers: {
      ...defaultHeaders,
      ...(options.headers || {}),
      ...dealerAdminHeaders(),
      ...supportHeadersForEndpoint(endpoint),
      ...(token
        ? {
            Authorization: `Bearer ${token}`,
            'X-Authorization': `Bearer ${token}`,
            'X-Forwarded-Authorization': `Bearer ${token}`,
            'X-Original-Authorization': `Bearer ${token}`,
            'X-Firebase-Authorization': `Bearer ${token}`,
            'X-User-Authorization': `Bearer ${token}`,
          }
        : {}),
    },
  });
};

/**
 * Make an authenticated API request with proper token handling
 * @param {string} endpoint - API endpoint (e.g., '/api/claims')
 * @param {Object} options - Fetch options
 * @param {string} options.method - HTTP method (GET, POST, PUT, DELETE)
 * @param {Object} options.body - Request body (will be JSON stringified)
 * @param {boolean} options.forceTokenRefresh - Whether to force a token refresh
 * @returns {Promise<Object>} - Response data
 */
function buildQueryString(params) {
  if (!params || typeof params !== 'object') return '';
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null && item !== '') {
          search.append(key, String(item));
        }
      });
      return;
    }
    search.set(key, String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export async function apiRequest(endpoint, options = {}) {
  const {
    method = 'GET',
    body,
    forceTokenRefresh = false,
    params,
    ...fetchOptions
  } = options;

  const currentUser = await resolvedAuthUser();

  if (!currentUser) {
    console.warn('🔒 No authenticated user found for API request');
    throw new Error('Not authenticated');
  }

  try {
    // Get a fresh token if requested
    const idToken = await currentUser.getIdToken(forceTokenRefresh);

    devLog(`[API] ${method} ${endpoint}`);

    // Prepare request headers
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...fetchOptions.headers,
      ...dealerAdminHeaders(),
      ...supportHeadersForEndpoint(endpoint),
      Authorization: `Bearer ${idToken}`,
      'X-Authorization': `Bearer ${idToken}`,
      'X-Forwarded-Authorization': `Bearer ${idToken}`,
      'X-Original-Authorization': `Bearer ${idToken}`,
      'X-Firebase-Authorization': `Bearer ${idToken}`,
      'X-User-Authorization': `Bearer ${idToken}`,
    };


    // Build request options
    // Exclude headers from fetchOptions to avoid overwriting merged headers
    const { headers: _, ...restFetchOptions } = fetchOptions;
    const requestOptions = {
      method,
      headers,
      ...restFetchOptions,
      // Include credentials if the backend uses cookies for any session features
      credentials: fetchOptions.credentials || 'include',
      // Ensure browser doesn't serve cached responses for API calls
      cache: 'no-store',
    };

    // Add body for non-GET requests
    if (body && method !== 'GET') {
      requestOptions.body = JSON.stringify(applyDealerTenantToPayload(body));
    }

    // Make the request — append query params (fetch ignores a raw `params` option)
    const scopedParams = applyDealerTenantToPayload(params);
    const url = `${getApiUrl(applySupportTenantToUrl(applyDealerTenantToUrl(endpoint)))}${buildQueryString(scopedParams)}`;
    const response = await fetch(url, requestOptions);
    const data = await response.json();

    // Check for API errors
    if (!response.ok) {
      const error = new Error(data.error || 'API request failed');
      error.response = { status: response.status, data };
      throw error;
    }

    return data;
  } catch (error) {

    // Check for token expiration
    if (
      error.code === 'auth/id-token-expired' ||
      (error.message?.includes('token') && error.message?.includes('expired'))
    ) {
      // Try once more with a forced refresh
      if (!forceTokenRefresh) {
        return apiRequest(endpoint, { ...options, forceTokenRefresh: true });
      }
    }

    // Handle network errors
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      console.error(`[API] Network error for ${endpoint}`);
      throw new Error(
        `Network error or CORS issue when trying to reach ${endpoint}. Please check your network connection.`
      );
    } else if (error.response?.status >= 400) {
      console.error(`[API] ${error.response.status} error for ${endpoint}`);
    }
    throw error;
  }
}

/**
 * Shorthand for GET requests with deduplication
 */
const __inflightGet = new Map();
export function get(endpoint, options = {}) {
  const { noDedupe = false, dedupeKey, ...restOptions } = options || {};
  // Include query params in the dedupe key — otherwise limit/page variants collide.
  const key =
    dedupeKey ||
    `${endpoint}${buildQueryString(restOptions.params)}`;
  devLog(`[DEDUPE] get() called for ${key}, map size: ${__inflightGet.size}, has key: ${__inflightGet.has(key)}`);
  if (!noDedupe) {
    const existing = __inflightGet.get(key);
    if (existing) {
      devLog(`[DEDUPE] ✓ Reusing in-flight GET for ${key}`);
      return existing;
    }
  }
  devLog(`[DEDUPE] Creating new promise for ${key}`);
  const p = apiRequest(endpoint, { ...restOptions, method: 'GET' })
    .finally(() => {
      if (__inflightGet.get(key) === p) {
        __inflightGet.delete(key);
        devLog(`[DEDUPE] Cleaned up ${key} from map`);
      }
    });
  if (!noDedupe) {
    __inflightGet.set(key, p);
    devLog(`[DEDUPE] ✓ Stored promise for ${key}, map size now: ${__inflightGet.size}`);
  }
  return p;
}

/**
 * Shorthand for POST requests
 */
export function post(endpoint, body, options = {}) {
  return apiRequest(endpoint, { ...options, method: 'POST', body });
}

/**
 * Shorthand for PUT requests
 */
export function put(endpoint, body, options = {}) {
  return apiRequest(endpoint, { ...options, method: 'PUT', body });
}

/**
 * Shorthand for DELETE requests
 */
export function del(endpoint, options = {}) {
  return apiRequest(endpoint, { ...options, method: 'DELETE' });
}

export function listDevices({ tenantId, status, type } = {}) {
  const params = new URLSearchParams();
  if (tenantId) params.set('tenantId', tenantId);
  if (status) params.set('status', status);
  if (type) params.set('type', type);
  const qs = params.toString();
  const path = `/api/devices${qs ? `?${qs}` : ''}`;
  return get(path, { headers: { 'Cache-Control': 'no-cache' } });
}

// Device Config Intelligence helpers
export function getDeviceSnapshots(deviceId) {
  const ts = Date.now();
  const path = `/api/v2/device-config/${encodeURIComponent(deviceId)}/snapshots?ts=${ts}`;
  return get(path, { headers: { 'Cache-Control': 'no-cache' } });
}

// Latest parsed config for a device
export function getDeviceParsed(deviceId, options = {}) {
  const path = `/api/v2/device-config/device/${encodeURIComponent(deviceId)}/parsed`;
  const headers = { 'Cache-Control': 'no-cache', ...(options.headers || {}) };
  const rest = { ...options, headers };
  return get(path, { ...rest, dedupeKey: path });
}

// Device Config Schema and Validation
export function getDeviceSchema(model, options = {}) {
  const path = `/api/v2/admin/devices/schema/${encodeURIComponent(model)}`;
  return get(path, options);
}

export function getDeviceConfigDiff(deviceId, options = {}) {
  const path = `/api/v2/admin/devices/${encodeURIComponent(deviceId)}/diff`;
  const headers = { 'Cache-Control': 'no-cache', ...(options.headers || {}) };
  return get(path, { ...options, headers });
}

// Admin-only: rebuild a model schema from latest parsed snapshot
export function rebuildModelSchema(model, options = {}) {
  const path = `/api/v2/device-models/${encodeURIComponent(model)}/schema/rebuild`;
  return post(path, {}, options);
}

export function processSnapshot(snapshotId) {
  const path = `/api/v2/device-config/${encodeURIComponent(snapshotId)}/process`;
  return post(path, {});
}

export function getParsedSnapshot(snapshotId) {
  const path = `/api/v2/device-config/${encodeURIComponent(snapshotId)}/parsed`;
  return get(path);
}

// Download raw snapshot text (tries top-level, falls back to legacy)
export async function getRawSnapshotText(snapshotId, deviceId) {
  // Try top-level first
  let res = await authenticatedFetch(`/api/v2/device-config/${encodeURIComponent(snapshotId)}/raw`, { method: 'GET' });
  if (res.ok) {
    return await res.text();
  }
  // Fallback to legacy path if deviceId provided
  if (deviceId) {
    res = await authenticatedFetch(`/api/v2/device-config/${encodeURIComponent(deviceId)}/snapshots/${encodeURIComponent(snapshotId)}/raw`, { method: 'GET' });
    if (res.ok) return await res.text();
  }
  throw new Error('Raw snapshot download failed');
}

// Delete a snapshot (tries top-level, falls back to legacy)
export async function deleteSnapshotSmart(snapshotId, deviceId) {
  try {
    await del(`/api/v2/device-config/${encodeURIComponent(snapshotId)}`);
    return { deleted: true, topLevel: true };
  } catch (e) {
    if (deviceId) {
      await del(`/api/v2/device-config/${encodeURIComponent(deviceId)}/snapshots/${encodeURIComponent(snapshotId)}`);
      return { deleted: true, topLevel: false };
    }
    throw e;
  }
}

// Bulk delete snapshots for a device
export function bulkDeleteSnapshots(deviceId, { keepLatest = 0, limit = 0 } = {}) {
  const path = `/api/v2/device-config/${encodeURIComponent(deviceId)}/snapshots`;
  return apiRequest(path, { method: 'DELETE', body: { keepLatest, limit } });
}

// Trigger agent to pull device config now and upload a snapshot
export function pullDeviceConfigNow(deviceId, { tenantId, username, password } = {}) {
  const path = `/api/v2/device-config/${encodeURIComponent(deviceId)}/pull-now`;
  const body = {};
  if (tenantId) body.tenantId = tenantId;
  if (username) body.username = username;
  if (password) body.password = password;
  return post(path, body);
}
